/**
 * The operations API, as the screens see it (P2-TR3).
 *
 * Quantities are strings for the same reason money is: a store whose stock is
 * a float produces totals that disagree with themselves (MONEY-1).
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { api } from '../../api/client.js';

export interface InventoryItem {
  id: number;
  name: string;
  itemType: 'feed' | 'medicine';
  unit: string;
  currentStock: string;
  avgUnitCost: string;
  reorderLevel: string;
  stockValue: string;
  isLow: boolean;
  isActive: boolean;
}

export interface DailyLog {
  id: number;
  date: string;
  cleaningDone: boolean | null;
  waterChecked: boolean | null;
  animalsInspected: boolean | null;
  electricityMeterReading: string | null;
  note: string | null;
  loggedBy: number | null;
  isComplete: boolean;
}

export interface Reminder {
  id: number;
  type: string;
  refTable: string | null;
  refId: number | null;
  dueDate: string;
  message: string;
  isDone: boolean;
  daysUntilDue: number;
}

interface Envelope<T> {
  success: true;
  data: T;
}

export const opsKeys = {
  inventory: () => ['inventory'] as const,
  dailyLog: (days: number) => ['operations', 'daily-log', days] as const,
  reminders: () => ['reminders'] as const,
  reminderCount: () => ['reminders', 'count'] as const,
};

export function useInventory(): UseQueryResult<InventoryItem[]> {
  return useQuery({
    queryKey: opsKeys.inventory(),
    queryFn: async () => (await api.get<Envelope<InventoryItem[]>>('/inventory')).data.data,
  });
}

/**
 * A new thing to stock, matching `createItemSchema` (BR-19).
 *
 * `currentStock` and `avgUnitCost` are absent and must stay absent. Both are
 * consequences of the movement history, and the server's `.strict()` schema
 * refuses either outright — letting one be set directly would break the one
 * invariant the whole module guarantees, that stock equals the sum of its
 * movements.
 */
export interface CreateItemInput {
  name: string;
  itemType: 'feed' | 'medicine';
  unit: 'kg' | 'litre' | 'piece' | 'dose';
  /** BR-20 — zero, or absent, means never alert. */
  reorderLevel?: string;
}

export function useCreateItem(): UseMutationResult<InventoryItem, unknown, CreateItemInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateItemInput) =>
      (await api.post<Envelope<InventoryItem>>('/inventory', input)).data.data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: opsKeys.inventory() });
    },
  });
}

/**
 * Feed arriving in the store, matching `purchaseSchema` (BR-19, P1-BR5).
 *
 * Two things happen on the server and both matter to the screen: stock goes
 * up by `qty`, and a *pending* expense is posted for `qty × unitPrice`. The
 * stock is real immediately; the money waits for a second partner (BR-07).
 */
export interface PurchaseInput {
  itemId: number;
  date: string;
  /** Quantity and price are strings all the way to the wire (MONEY-1). */
  qty: string;
  unitPrice: string;
  /** Absent means bought on credit — a baki at the feed shop (P1-BR5). */
  accountId?: number;
  categoryId?: number;
  vendorId?: number;
  /** BR-22 — medicines only; the server refuses it on feed. */
  expiryDate?: string;
  note?: string;
}

export function useRecordPurchase(): UseMutationResult<unknown, unknown, PurchaseInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: PurchaseInput) => (await api.post('/inventory/purchase', input)).data,
    onSuccess: async () => {
      // The store and the money both moved, so both are stale.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: opsKeys.inventory() }),
        queryClient.invalidateQueries({ queryKey: ['transactions'] }),
        queryClient.invalidateQueries({ queryKey: ['accounts'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'finance'] }),
      ]);
    },
  });
}

/**
 * A physical count, matching `adjustStockSchema` (BR-21).
 *
 * The figure sent is what was *counted*, not a difference — the server works
 * out the delta against what it expected and writes that as a movement. This
 * is why the reason is mandatory: months later, a correction with no
 * explanation is indistinguishable from a data-entry mistake.
 */
export interface AdjustStockInput {
  itemId: number;
  date: string;
  countedQty: string;
  note: string;
}

export function useAdjustStock(): UseMutationResult<
  { difference: string; item: InventoryItem },
  unknown,
  AdjustStockInput
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, ...body }: AdjustStockInput) =>
      (
        await api.post<Envelope<{ difference: string; item: InventoryItem }>>(
          `/inventory/${String(itemId)}/adjust`,
          body,
        )
      ).data.data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: opsKeys.inventory() });
    },
  });
}

export function useRecentDailyLogs(days = 14): UseQueryResult<{ date: string; log: DailyLog | null }[]> {
  return useQuery({
    queryKey: opsKeys.dailyLog(days),
    queryFn: async () =>
      (await api.get<Envelope<{ date: string; log: DailyLog | null }[]>>('/operations/daily-log', { params: { days } }))
        .data.data,
  });
}

export interface DailyLogInput {
  date: string;
  cleaningDone?: boolean;
  waterChecked?: boolean;
  animalsInspected?: boolean;
  electricityMeterReading?: string;
  note?: string;
}

export function useSaveDailyLog(): UseMutationResult<DailyLog, unknown, DailyLogInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: DailyLogInput) =>
      (await api.post<Envelope<DailyLog>>('/operations/daily-log', input)).data.data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['operations', 'daily-log'] });
    },
  });
}

export interface MilkLogInput {
  date: string;
  session: 'morning' | 'evening';
  lines: { animalId: number; litres: string }[];
}

export function useSaveMilkLog(): UseMutationResult<{ totalLitres: string }, unknown, MilkLogInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: MilkLogInput) =>
      (await api.post<Envelope<{ totalLitres: string }>>('/operations/milk-log', input)).data.data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['operations'] });
    },
  });
}

export function useReminders(): UseQueryResult<Reminder[]> {
  return useQuery({
    queryKey: opsKeys.reminders(),
    queryFn: async () => (await api.get<Envelope<Reminder[]>>('/reminders')).data.data,
  });
}

export function useReminderCount(): UseQueryResult<{ open: number }> {
  return useQuery({
    queryKey: opsKeys.reminderCount(),
    queryFn: async () => (await api.get<Envelope<{ open: number }>>('/reminders/count')).data.data,
    // The badge is checked often and matters little if it is a minute stale.
    staleTime: 60_000,
  });
}

export function useCompleteReminder(): UseMutationResult<Reminder, unknown, number> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => (await api.post<Envelope<Reminder>>(`/reminders/${String(id)}/done`)).data.data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['reminders'] });
    },
  });
}
